/**
 * Campus Valley 360° — Page Pilier (tronc commun).
 * Liste des leçons du pilier avec progression individuelle et navigation vers les leçons.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    ArrowLeft, CheckCircle2, Circle, Clock, Video, Headphones, FileText, Sparkles,
    PlayCircle, Loader2, Award, Download,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CampusPillar() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { session } = useAuth();
    const { lang } = useLang();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloadingCert, setDownloadingCert] = useState(false);

    useEffect(() => {
        if (!session) return;
        (async () => {
            try {
                const r = await axios.get(`${API}/campus/pillars/${slug}`, {
                    headers: { Authorization: `Bearer ${session.access_token}` },
                });
                setData(r.data);
            } catch (e) {
                if (e.response?.status === 404) toast.error(lang === 'fr' ? 'Pilier introuvable' : 'Pillar not found');
                else toast.error(e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [slug, session, lang]);

    if (loading) {
        return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>;
    }
    if (!data?.pillar) {
        return (
            <div className="text-center py-20">
                <div className="text-2xl font-heading mb-3">{lang === 'fr' ? 'Pilier introuvable' : 'Pillar not found'}</div>
                <Link to="/app/formation" className="text-primary hover:underline">← {lang === 'fr' ? 'Retour au Campus' : 'Back to Campus'}</Link>
            </div>
        );
    }

    const { pillar, lessons } = data;
    const totalLessons = lessons.length;
    const doneLessons = lessons.filter(l => l.user_progress?.status === 'completed').length;
    const pct = totalLessons ? Math.round((doneLessons / totalLessons) * 100) : 0;
    const accent = pillar.color_hex || '#C8553D';
    const canCertify = totalLessons > 0 && doneLessons === totalLessons;

    const downloadCert = async () => {
        setDownloadingCert(true);
        try {
            const r = await axios.get(`${API}/campus/pillars/${slug}/certificate.pdf`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                responseType: 'blob',
            });
            const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `campus-pilier-${pillar.order_index}-${slug}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(lang === 'fr' ? 'Certificat téléchargé !' : 'Certificate downloaded!');
        } catch (e) {
            const detail = e.response?.data instanceof Blob
                ? await e.response.data.text().then(t => { try { return JSON.parse(t).detail; } catch { return t; } })
                : e.response?.data?.detail || e.message;
            toast.error(detail || 'Erreur');
        }
        setDownloadingCert(false);
    };

    return (
        <div className="space-y-8" data-testid={`pillar-page-${pillar.slug}`}>
            <Link to="/app/formation" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="pillar-back">
                <ArrowLeft size={14} className="mr-1.5" /> {lang === 'fr' ? 'Tous les piliers' : 'All pillars'}
            </Link>

            {/* Header */}
            <div className="rounded-3xl p-10 lg:p-14 relative overflow-hidden text-white" style={{ background: `linear-gradient(135deg, ${accent}, #1c1a19 85%)` }}>
                <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
                <div className="relative">
                    <div className="text-xs uppercase tracking-[0.25em] text-white/60 mb-3 font-mono">
                        Pilier {pillar.order_index} · {lang === 'fr' ? 'Tronc commun' : 'Core curriculum'}
                    </div>
                    <h1 className="font-heading text-4xl lg:text-5xl leading-tight mb-4">
                        {lang === 'fr' ? pillar.title_fr : pillar.title_en}
                    </h1>
                    <p className="text-white/75 max-w-3xl text-lg leading-relaxed">
                        {lang === 'fr' ? pillar.summary_fr : pillar.summary_en}
                    </p>

                    <div className="grid md:grid-cols-2 gap-6 mt-8">
                        {pillar.objectives_fr && (
                            <div>
                                <div className="text-[11px] uppercase tracking-wider text-white/50 mb-2">{lang === 'fr' ? 'Objectifs' : 'Objectives'}</div>
                                <div className="text-sm text-white/80 leading-relaxed">{lang === 'fr' ? pillar.objectives_fr : pillar.objectives_en}</div>
                            </div>
                        )}
                        {pillar.deliverables_fr && (
                            <div>
                                <div className="text-[11px] uppercase tracking-wider text-white/50 mb-2">{lang === 'fr' ? 'Livrables' : 'Deliverables'}</div>
                                <div className="text-sm text-white/80 leading-relaxed">{lang === 'fr' ? pillar.deliverables_fr : pillar.deliverables_en}</div>
                            </div>
                        )}
                    </div>

                    {totalLessons > 0 && (
                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <div className="inline-flex items-center gap-4 px-5 py-3 rounded-full bg-white/10 backdrop-blur-md">
                                <Award size={18} className="text-white/80" />
                                <div className="text-sm">
                                    <span className="font-heading text-2xl text-white tabular-nums mr-2">{pct}%</span>
                                    <span className="text-white/60">{doneLessons}/{totalLessons} {lang === 'fr' ? 'leçons' : 'lessons'}</span>
                                </div>
                            </div>
                            {canCertify && (
                                <Button onClick={downloadCert} disabled={downloadingCert}
                                        className="bg-white text-ink hover:bg-white/90 font-medium" data-testid="pillar-cert-download">
                                    {downloadingCert ? <Loader2 size={14} className="animate-spin mr-2" /> : <Download size={14} className="mr-2" />}
                                    {lang === 'fr' ? 'Télécharger mon certificat' : 'Download certificate'}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Lessons list */}
            <div>
                <h2 className="font-heading text-2xl mb-5">{lang === 'fr' ? 'Programme' : 'Curriculum'}</h2>
                {lessons.length === 0 ? (
                    <div className="card-soft p-8 text-center text-foreground/60" data-testid="pillar-lessons-empty">
                        {lang === 'fr'
                            ? 'Aucune leçon publiée pour ce pilier. Le Super Admin peut ajouter des leçons (vidéo, audio, PDF, quiz).'
                            : 'No published lessons yet. Super Admin can add lessons (video, audio, PDF, quiz).'}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {lessons.map((lesson, idx) => {
                            const completed = lesson.user_progress?.status === 'completed';
                            let readingPct = null;
                            if (!completed) {
                                try {
                                    const v = JSON.parse(localStorage.getItem(`av-lesson-pos-${lesson.id}`) || 'null');
                                    if (v && typeof v === 'object' && v.pct) readingPct = v.pct;
                                } catch { /* ignore */ }
                            }
                            const hasVideo = !!lesson.video_url;
                            const hasAudio = !!lesson.audio_url;
                            const hasPdf = !!lesson.pdf_url;
                            return (
                                <button
                                    type="button"
                                    key={lesson.id}
                                    onClick={() => navigate(`/app/formation/lecon/${lesson.id}`)}
                                    className="w-full text-left card-soft p-5 flex items-center gap-5 hover:border-primary/40 hover:translate-x-1 transition-all duration-300 group"
                                    data-testid={`lesson-row-${lesson.slug}`}
                                >
                                    <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${completed ? 'bg-emerald-50 text-emerald-600' : 'bg-surface-muted text-muted-foreground'}`}>
                                        {completed ? <CheckCircle2 size={20} /> : <Circle size={18} />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-1">
                                            <span className="text-xs font-mono text-muted-foreground">L{idx + 1}</span>
                                            <h3 className="font-heading text-lg leading-tight truncate">{lang === 'fr' ? lesson.title_fr : lesson.title_en}</h3>
                                            {readingPct && (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium shrink-0" data-testid={`lesson-resume-badge-${lesson.slug}`}>
                                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                                                    {lang === 'fr' ? 'En cours' : 'In progress'} · {readingPct}%
                                                </span>
                                            )}
                                        </div>
                                        {lesson.summary_fr && (
                                            <div className="text-sm text-foreground/65 line-clamp-1">{lang === 'fr' ? lesson.summary_fr : lesson.summary_en}</div>
                                        )}
                                        <div className="flex items-center gap-3 mt-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                                            {lesson.duration_min && <span className="inline-flex items-center"><Clock size={11} className="mr-1" /> {lesson.duration_min} min</span>}
                                            {hasVideo && <span className="inline-flex items-center text-primary"><Video size={11} className="mr-1" /> Vidéo</span>}
                                            {hasAudio && <span className="inline-flex items-center text-secondary"><Headphones size={11} className="mr-1" /> Audio</span>}
                                            {hasPdf && <span className="inline-flex items-center text-foreground/60"><FileText size={11} className="mr-1" /> PDF</span>}
                                            {(lesson.quiz || []).length > 0 && <span className="inline-flex items-center text-amber-600">Quiz · {lesson.quiz.length} Q</span>}
                                            {lesson.ai_enabled && <span className="inline-flex items-center text-violet-600"><Sparkles size={11} className="mr-1" /> IA</span>}
                                        </div>
                                    </div>
                                    <PlayCircle size={22} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
